import { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import OtpInput from "react-otp-input";
import { logo } from "../../../assets";
import { loginUserValidation } from "../../../utils/validation/validation";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import ValidationErrorMessage from "./../../../helpers/ValidationErrorMessage/ValidationErrorMessage";
import { authContext } from "../../../context/AuthContext/AuthContext";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { MdInstallDesktop, MdInstallMobile } from "react-icons/md";

const SignIn = () => {
  const {
    isShowPassLogin,
    setIsShowPassLogin,
    loginCode,
    setLoginCode,
    sendCode,
    handleLogIn,
    handleOtpCode,
    setDeferredPrompt,
    showInstallButton,
    setShowInstallButton,
    handleInstallClick,
  } = useContext(authContext);

  // PWA Installer
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  // Validation
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginUserValidation),
  });

  return (
    <>
      <SiteTitle title="ورود به حساب کاربری" />
      <div className="bg-gray-300 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-gray-200 transition-transform duration-300 hover:scale-[1.01]">
            {/* Logo Header */}
            <div className="bg-gray-100 py-6 flex flex-col items-center border-b border-gray-200">
              <div className="relative w-20 h-20 flex items-center justify-center bg-white border-2 border-yellow-500 rounded-lg shadow-sm overflow-hidden">
                <img
                  src={logo}
                  alt="Website Logo"
                  className="object-contain w-16 h-16"
                />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mt-4">ورود به حساب کاربری</h2>
              <p className="text-gray-500 text-sm mt-1">رهبانی</p>
            </div>

            <div className="p-6">
              <div className={`${sendCode ? "hidden" : "block"}`}>
                <form onSubmit={handleSubmit(handleLogIn)} className="space-y-4">
                  {/* Phone Number */}
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      شماره موبایل
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        name="phoneNumber"
                        placeholder="09123456789"
                        autoComplete="off"
                        {...register("phonenumber")}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg 
                          text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 
                          focus:ring-yellow-500 focus:border-transparent"
                      />
                    </div>
                    <ValidationErrorMessage message={errors.phonenumber?.message} />
                  </div>

                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      رمز عبور
                    </label>
                    <div className="relative">
                      <input
                        type={isShowPassLogin ? "text" : "password"}
                        name="password"
                        placeholder="••••••"
                        autoComplete="off"
                        {...register("password")}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg 
                          text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 
                          focus:ring-yellow-500 focus:border-transparent pr-12"
                      />
                      <button
                        type="button"
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-yellow-600"
                        onClick={() => setIsShowPassLogin(!isShowPassLogin)}
                      >
                        {isShowPassLogin ? (
                          <HiEye size={20} className="text-gray-500" />
                        ) : (
                          <HiEyeOff size={20} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                    <ValidationErrorMessage message={errors.password?.message} />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-medium 
                      rounded-lg transition duration-200 flex items-center justify-center shadow-md"
                  >
                    دریافت کد
                  </button>
                </form>

                {/* Buttons Below Form */}
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <Link to="/Sign-Up">
                    <button className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium 
                      rounded-lg transition duration-200 shadow">
                      ثبت نام
                    </button>
                  </Link>
                  <Link to="/Forget-Password">
                    <button className="w-full py-3 bg-red-500 hover:bg-red-600 text-white font-medium 
                      rounded-lg transition duration-200 shadow">
                      فراموشی رمز
                    </button>
                  </Link>
                </div>
              </div>

              {/* Verify Code Section */}
              <div className={`${sendCode ? "block" : "hidden"}`}>
                <form onSubmit={handleSubmit(handleOtpCode)}>
                  <div className="space-y-4">
                    <label htmlFor="verifyCode" className="block text-sm font-medium text-gray-700 mb-2">
                      کد تأیید
                    </label>
                    <div className="flex justify-center" style={{ direction: "ltr" }}>
                      <OtpInput
                        value={loginCode}
                        onChange={setLoginCode}
                        numInputs={5}
                        renderInput={(props) => (
                          <input
                            {...props}
                            className="w-12 h-12 mx-1 text-center bg-gray-50 border border-gray-300 
                              rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 
                              focus:border-transparent"
                            style={{ direction: "rtl" }}
                          />
                        )}
                        containerStyle="flex justify-center"
                      />
                    </div>
                    <p className="text-center text-gray-600 text-sm mt-2">
                      زمان باقی مانده: <span id="verificationCodeCountDown" className="text-yellow-600 font-medium">120</span> ثانیه
                    </p>
                    <button
                      type="submit"
                      className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-medium 
                        rounded-lg transition duration-200 shadow-md"
                    >
                      ورود
                    </button>
                  </div>
                </form>
              </div>

              {/* PWA Install Button */}
              {showInstallButton && (
                <button
                  onClick={handleInstallClick}
                  className="w-full mt-6 py-3 bg-gray-800 hover:bg-gray-700 text-white 
                    font-medium rounded-lg transition duration-200 flex items-center justify-center shadow-md"
                >
                  {window.innerWidth > 768 ? (
                    <MdInstallDesktop size={20} className="ml-2 text-white" />
                  ) : (
                    <MdInstallMobile size={20} className="ml-2 text-white" />
                  )}
                  نصب نسخه تحت وب
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;